import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FiX, FiCheck, FiMove, FiSliders, FiEyeOff, FiCornerDownRight, FiCornerDownLeft, FiCornerUpRight, FiCornerUpLeft } from 'react-icons/fi';

/**
 * LogoPreviewModal - Interactive logo placement preview
 * 
 * Allows users to:
 * - See AI-suggested placement
 * - Drag logo to custom position
 * - Adjust size and opacity
 * - Skip logo entirely
 * - Apply to all images in batch
 */
const LogoPreviewModal = ({ 
  isOpen, 
  onClose, 
  imageUrl, 
  logoUrl,
  aiSuggestion,
  onApply,
  onSkip,
  onApplyToAll,
  imageIndex,
  totalImages,
  clientName
}) => {
  // Logo position state (percentages)
  const [position, setPosition] = useState({
    x: aiSuggestion?.position_x_percent || 88,
    y: aiSuggestion?.position_y_percent || 92
  });
  const [size, setSize] = useState((aiSuggestion?.size_percent || 0.10) * 100);
  const [opacity, setOpacity] = useState((aiSuggestion?.opacity || 0.5) * 100);
  const [isDragging, setIsDragging] = useState(false);
  const [applyToAllChecked, setApplyToAllChecked] = useState(false);
  
  const containerRef = useRef(null);
  const logoRef = useRef(null);

  // Update state when AI suggestion changes
  useEffect(() => {
    if (aiSuggestion) {
      setPosition({
        x: aiSuggestion.position_x_percent || 88,
        y: aiSuggestion.position_y_percent || 92
      });
      setSize((aiSuggestion.size_percent || 0.10) * 100);
      setOpacity((aiSuggestion.opacity || 0.5) * 100);
    }
  }, [aiSuggestion]);

  // Handle drag start
  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  // Handle drag move
  const handleDragMove = useCallback((e) => {
    if (!isDragging || !containerRef.current) return;
    
    const container = containerRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    // Calculate position as percentage
    let x = ((clientX - container.left) / container.width) * 100;
    let y = ((clientY - container.top) / container.height) * 100;
    
    // Clamp to bounds
    x = Math.max(5, Math.min(95, x));
    y = Math.max(5, Math.min(95, y));
    
    setPosition({ x, y });
  }, [isDragging]);

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add/remove event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove);
      window.addEventListener('touchend', handleDragEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchmove', handleDragMove);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  // Preset positions
  const presetPositions = [
    { id: 'top_left', x: 12, y: 12, icon: FiCornerUpLeft, label: 'Top Left' },
    { id: 'top_right', x: 88, y: 12, icon: FiCornerUpRight, label: 'Top Right' },
    { id: 'bottom_left', x: 12, y: 88, icon: FiCornerDownLeft, label: 'Bottom Left' },
    { id: 'bottom_right', x: 88, y: 88, icon: FiCornerDownRight, label: 'Bottom Right' },
  ];

  const handleApply = () => {
    const settings = {
      position_x_percent: position.x,
      position_y_percent: position.y,
      size_percent: size / 100,
      opacity: opacity / 100
    };
    
    if (applyToAllChecked && onApplyToAll) {
      onApplyToAll(settings);
    } else {
      onApply(settings);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] p-4">
      <div className="bg-card rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-border">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Logo Placement Preview</h2>
            {totalImages > 1 && (
              <p className="text-sm text-muted-foreground">
                Image {imageIndex + 1} of {totalImages} • {clientName}
              </p>
            )}
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-secondary rounded-lg transition-colors"
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="flex flex-col lg:flex-row">
          {/* Image Preview */}
          <div className="flex-1 p-4">
            <div 
              ref={containerRef}
              className="relative bg-secondary/50 rounded-lg overflow-hidden aspect-square"
              style={{ cursor: isDragging ? 'grabbing' : 'default' }}
            >
              {/* Generated Image */}
              <img 
                src={imageUrl} 
                alt="Generated content"
                className="w-full h-full object-contain"
                draggable={false}
              />
              
              {/* Logo Overlay */}
              {logoUrl && (
                <div
                  ref={logoRef}
                  className={`absolute cursor-grab active:cursor-grabbing transition-opacity ${
                    isDragging ? 'ring-2 ring-primary ring-offset-2' : ''
                  }`}
                  style={{
                    left: `${position.x}%`,
                    top: `${position.y}%`,
                    transform: 'translate(-50%, -50%)',
                    width: `${size}%`,
                    opacity: opacity / 100
                  }}
                  onMouseDown={handleDragStart}
                  onTouchStart={handleDragStart}
                >
                  <img 
                    src={logoUrl} 
                    alt="Brand logo"
                    className="w-full h-auto pointer-events-none"
                    draggable={false}
                  />
                  {/* Drag handle indicator */}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 hover:opacity-100 transition-opacity rounded">
                    <FiMove className="text-white" size={24} />
                  </div>
                </div>
              )}
            </div>
            
            {/* AI Suggestion */}
            {aiSuggestion?.reason && (
              <div className="mt-3 p-3 bg-secondary/50 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">AI Suggestion:</span> {aiSuggestion.reason}
                </p>
              </div>
            )}
          </div>

          {/* Controls Panel */}
          <div className="w-full lg:w-72 p-4 border-t lg:border-t-0 lg:border-l border-border bg-secondary/20">
            <div className="space-y-5">
              {/* Position Presets */}
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Position
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {presetPositions.map((preset) => {
                    const Icon = preset.icon;
                    const isActive = Math.abs(position.x - preset.x) < 10 && Math.abs(position.y - preset.y) < 10;
                    return (
                      <button
                        key={preset.id}
                        onClick={() => setPosition({ x: preset.x, y: preset.y })}
                        className={`p-3 rounded-lg border transition-all ${
                          isActive 
                            ? 'border-primary bg-primary/10 text-primary' 
                            : 'border-border hover:border-primary/50 text-muted-foreground'
                        }`}
                        title={preset.label}
                      >
                        <Icon size={18} className="mx-auto" />
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Or drag the logo to position it
                </p>
              </div>

              {/* Size Slider */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-foreground">Size</label>
                  <span className="text-sm text-muted-foreground">{Math.round(size)}%</span>
                </div>
                <input
                  type="range"
                  min="5"
                  max="25"
                  value={size}
                  onChange={(e) => setSize(Number(e.target.value))}
                  className="w-full h-2 bg-secondary rounded-lg appearance-none cursor-pointer accent-primary"
                />
              </div>

              {/* Opacity Slider */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-foreground">Opacity</label>
                  <span className="text-sm text-muted-foreground">{Math.round(opacity)}%</span>
                </div>
                <input
                  type="range"
                  min="20"
                  max="100"
                  value={opacity}
                  onChange={(e) => setOpacity(Number(e.target.value))}
                  className="w-full h-2 bg-secondary rounded-lg appearance-none cursor-pointer accent-primary"
                />
              </div>

              {/* Apply to All Checkbox */}
              {totalImages > 1 && (
                <label className="flex items-center gap-2 cursor-pointer p-3 bg-secondary/50 rounded-lg">
                  <input
                    type="checkbox"
                    checked={applyToAllChecked}
                    onChange={(e) => setApplyToAllChecked(e.target.checked)}
                    className="w-4 h-4 accent-primary"
                  />
                  <span className="text-sm text-foreground">
                    Apply to all {totalImages} images
                  </span>
                </label>
              )}

              {/* Action Buttons */}
              <div className="space-y-2 pt-2">
                <button
                  onClick={handleApply}
                  className="w-full py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
                >
                  <FiCheck size={18} />
                  {applyToAllChecked ? 'Apply to All' : 'Apply Logo'}
                </button>
                
                <button
                  onClick={onSkip}
                  className="w-full py-3 bg-secondary text-foreground rounded-lg font-medium hover:bg-secondary/80 transition-colors flex items-center justify-center gap-2"
                >
                  <FiEyeOff size={18} />
                  Skip Logo
                </button>
              </div>

              {/* Position Display */}
              <div className="text-xs text-muted-foreground text-center pt-2 border-t border-border">
                Position: {Math.round(position.x)}% × {Math.round(position.y)}%
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default LogoPreviewModal;
